// src/pages/Exams/ExamSetDetail.tsx
import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { ExamGenApi, type ExamSetForm } from './generate/client';
import { Badge, EmptyState, ErrorPanel, KpiCard, PageHeader, PagedFooter, PanelCard, Segmented, Spinner, Table, TextLink, Th, Td, Track } from '../../school_panel/components/ui';
import { ArrowRight, BarChart3, Clock, Printer } from 'lucide-react';
import { Keys } from '../../school_panel/api/hooks';
import { SchoolApi } from '../../school_panel/api/client';
import { useSchoolChapters } from './generate/hooks/useSchoolChapters';
import { FaFileAlt } from '../../school_panel/components/ui/icons';
import type { Blueprint } from './generate/types';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { useGrades, useGradeSubjects } from '../../react-query/queries';
import { usePagination } from '../../school_panel/lib/usePagination';
import { LINK_BTN } from '../../school_panel/components/ui/linkButton';

const TRAIL = <>Exams / Exam set</>;

/** Only the blueprint fields the stored snapshot actually carries, as label/value pairs. */
export function blueprintFacts(bp: Partial<Blueprint> | null | undefined): Array<{ label: string; value: string }> {
  if (!bp) return [];
  const facts: Array<{ label: string; value: string }> = [];
  if (typeof bp.total_questions === 'number') facts.push({ label: 'Questions per form', value: String(bp.total_questions) });
  if (typeof bp.duration_min === 'number') facts.push({ label: 'Duration', value: `${bp.duration_min} min` });
  if (bp.difficulty_mode) facts.push({ label: 'Difficulty', value: bp.difficulty_mode });
  const d = bp.difficulty_pct;
  if (d && typeof d.easy === 'number' && typeof d.medium === 'number' && typeof d.hard === 'number') {
    facts.push({ label: 'Easy / medium / hard', value: `${d.easy}% / ${d.medium}% / ${d.hard}%` });
  }
  if (Array.isArray(bp.chapter_distribution) && bp.chapter_distribution.length > 0) {
    facts.push({ label: 'Chapters', value: String(bp.chapter_distribution.length) });
  }
  return facts;
}

const RBTN = 'inline-grid h-9 w-9 shrink-0 place-items-center rounded-[10px] border border-transparent text-muted-2 transition-colors hover:border-line-strong hover:bg-ground hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';
const TILE = 'min-w-0 rounded-[14px] border border-track bg-panel p-3.5';
const SL = 'font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

const dmy = (iso: string | null | undefined, withYear = false) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(undefined, withYear ? { day: 'numeric', month: 'short', year: 'numeric' } : { day: 'numeric', month: 'short' }) : null;
};

/** Figures over the per-form fields of a set; null parts when the backend sent none. */
export function setTotals(forms: ExamSetForm[]) {
  const withData = forms.filter((f) => f.took != null && f.assigned != null);
  const active = forms.filter((f) => f.status === 'Active').length;
  const drafts = forms.filter((f) => f.status === 'Draft');
  const avgs = forms.filter((f) => f.avg_score != null && (f.took ?? 0) > 0);
  const tookSum = avgs.reduce((n, f) => n + (f.took as number), 0);
  const mean = tookSum > 0 ? avgs.reduce((n, f) => n + (f.avg_score as number) * (f.took as number), 0) / tookSum : null;
  const spread = avgs.length >= 2 ? Math.max(...avgs.map((f) => f.avg_score as number)) - Math.min(...avgs.map((f) => f.avg_score as number)) : null;
  return {
    hasPerForm: withData.length > 0,
    hasStatus: forms.some((f) => f.status != null),
    active, drafts,
    took: withData.reduce((n, f) => n + (f.took as number), 0),
    assigned: withData.reduce((n, f) => n + (f.assigned as number), 0),
    mean, spread,
  };
}

/** Chapter x form table of % correct (design Compare view), one report request per form. */
function CompareView({ forms }: { forms: ExamSetForm[] }) {
  const reports = useQueries({
    queries: forms.map((f) => ({
      queryKey: Keys.examReport(f.exam_id, { section: 'all' }),
      queryFn: () => SchoolApi.examReport(f.exam_id, { section: 'all' }),
    })),
  });
  if (reports.some((r) => r.isLoading)) return <div role="status" className="flex justify-center py-10"><Spinner /></div>;
  if (reports.some((r) => r.isError)) return <p role="alert" className="rounded-[12px] bg-danger-bg px-4 py-3 text-sm text-danger-ink">Could not load the comparison.</p>;
  const names = new Map<number, { name: string; q: number }>();
  const pct = new Map<number, Map<number, number | null>>();
  reports.forEach((r, i) => {
    const m = new Map<number, number | null>();
    for (const c of r.data?.chapters ?? []) {
      if (!names.has(c.chapter_id)) names.set(c.chapter_id, { name: c.chapter_name, q: c.question_count });
      m.set(c.chapter_id, c.class_avg);
    }
    pct.set(i, m);
  });
  if (names.size === 0) return <EmptyState compact icon={<BarChart3 size={18} strokeWidth={1.8} aria-hidden />} title="Nothing to compare yet" body="Chapter results appear once students have taken the forms." />;
  const cell = (v: number | null | undefined) => (v == null ? <span className="text-muted">{'\u2014'}</span>
    : <Badge tone={v < 50 ? 'accent' : 'neutral'} className="font-mono">{Math.round(v)}%</Badge>);
  return (
    <>
      <p className="mb-3.5 text-[13px] text-muted">% correct per chapter on each form. Orange cells are below the 50% pass mark; a gap of more than 10 points means one form was harder on that chapter.</p>
      <Table minWidth={620}>
        <thead>
          <tr>
            <Th>Chapter</Th><Th align="right">Qs / form</Th>
            {forms.map((f) => <Th key={f.exam_id} align="right">Form {f.form_label}</Th>)}
            <Th align="right">Gap</Th>
          </tr>
        </thead>
        <tbody>
          {Array.from(names.entries()).map(([cid, info]) => {
            const vals = forms.map((_, i) => pct.get(i)?.get(cid));
            const nums = vals.filter((v): v is number => typeof v === 'number');
            const gap = nums.length >= 2 ? Math.max(...nums) - Math.min(...nums) : null;
            return (
              <tr key={cid}>
                <Td className="font-semibold">{info.name}</Td>
                <Td align="right" className="font-mono font-medium">{info.q}</Td>
                {vals.map((v, i) => <Td key={forms[i].exam_id} align="right">{cell(v)}</Td>)}
                <Td align="right">{gap == null ? <span className="text-muted">{'\u2014'}</span> : <Badge tone={gap > 10 ? 'warning' : 'neutral'} className="font-mono">{gap.toFixed(0)} pts</Badge>}</Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

export default function ExamSetDetail() {
  const { examSetId } = useParams<{ examSetId: string }>();
  const id = examSetId ? Number(examSetId) : 0;
  const { data, isLoading, error } = useQuery({
    queryKey: ['exam_gen', 'exam_set', id],
    queryFn: () => ExamGenApi.examSetDetail(id),
    enabled: id > 0,
    // A missing or out-of-scope set will not appear on retry; show the answer at once.
    retry: (n, err) => {
      const st = (err as { response?: { status?: number } } | null)?.response?.status;
      return !(st && st >= 400 && st < 500) && n < 2;
    },
  });

  const { token } = useAuth();
  const bpGrade = data?.blueprint?.grade_id ?? null;
  const bpSubject = data?.blueprint?.subject_id ?? null;
  const { data: gradeData } = useGrades(token);
  const { data: subjectData } = useGradeSubjects(token, bpGrade != null ? String(bpGrade) : '0');
  const gradeName = (gradeData?.grades ?? []).find((g: any) => g.id === bpGrade)?.grade_name;
  const subjectName = (subjectData?.subjects ?? []).find((x: any) => x.id === bpSubject)?.subject_name;
  const pg = usePagination(data?.memberships ?? [], 10);
  const [view, setView] = useState<'forms' | 'compare'>('forms');
  const chaptersQ = useSchoolChapters(bpSubject);

  if (isLoading) {
    return (
      <>
        <PageHeader eyebrow={TRAIL} title="Exam set" subtitle="The forms generated together as one set." />
        <div role="status" className="flex justify-center py-16"><Spinner /></div>
      </>
    );
  }
  const notFound = (error as { response?: { status?: number } } | null)?.response?.status === 404;
  if (error || (!data && id > 0 && !isLoading)) {
    return (
      <>
        <PageHeader eyebrow={TRAIL} title="Exam set" subtitle="The forms generated together as one set." />
        <PanelCard>
          {notFound || !error ? (
            <EmptyState
              icon={<FaFileAlt />}
              title="Exam set not found"
              body="It may have been removed, or it may hold exams outside your classes."
            />
          ) : (
            <ErrorPanel title="Could not load this exam set" body="Go back to the exam list and try again." />
          )}
          <div className="flex justify-center pb-6">
            <Link to="/exams/exam-list" className="inline-flex min-h-[44px] items-center justify-center rounded-[12px] border border-line-strong bg-surface px-4 py-2 text-sm font-semibold text-ink transition hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              Back to Exam list
            </Link>
          </div>
        </PanelCard>
      </>
    );
  }
  if (!data) return null;

  const forms = data.memberships.length;
  const totals = setTotals(data.memberships);
  const facts = [
    ...(subjectName ? [{ label: 'Subject', value: String(subjectName) }] : []),
    ...(gradeName ? [{ label: 'Grade', value: /^grade\s/i.test(String(gradeName)) ? String(gradeName) : `Grade ${gradeName}` }] : []),
    ...blueprintFacts(data.blueprint).filter(f => f.label !== 'Easy / medium / hard' && f.label !== 'Chapters'),
  ];
  const pct = data.blueprint?.difficulty_pct;
  const mix = pct && typeof pct.easy === 'number' && typeof pct.medium === 'number' && typeof pct.hard === 'number' ? pct : null;
  const chapterRows = (data.blueprint?.chapter_distribution ?? []).map((c) => ({
    id: c.chapter_id, count: c.count,
    name: (chaptersQ.data ?? []).find((x: any) => x.id === c.chapter_id)?.name as string | undefined,
  }));
  const hasBlueprint = facts.length > 0 || mix != null || chapterRows.length > 0;
  // "one form per section" only when every form names exactly one section and no two share it.
  const sectionNames = data.memberships.map((m) => m.section_names);
  const onePerSection = forms > 1 && sectionNames.every((n) => n != null && n.length === 1) && new Set(sectionNames.map((n) => n![0])).size === forms;
  const created = dmy(data.created_at, true);
  const unpublished = totals.drafts.map((f) => f.form_label);
  const tookPct = totals.assigned > 0 ? (totals.took / totals.assigned) * 100 : 0;
  return (
    <>
      <PageHeader
        eyebrow={<><Link className="hover:text-ink" to="/dashboard">Dashboard</Link> / <Link className="hover:text-ink" to="/exams/exam-list">Exams</Link> / Exam set</>}
        title={data.name}
        subtitle={`Created ${created ?? ''} · ${forms} form${forms === 1 ? '' : 's'}${forms > 0 ? ` · one blueprint${onePerSection ? ', one form per section' : ''}` : ''}`}
        actions={
          <>
            {data.generation_job_id != null && (
              <Link to={`/exams/generate/job/${data.generation_job_id}`} className={LINK_BTN}>
                <Clock size={18} strokeWidth={1.8} aria-hidden="true" />Generation run
              </Link>
            )}
            <Link to="/exams/paper" className={LINK_BTN}>
              <Printer size={18} strokeWidth={1.8} aria-hidden="true" />Print forms
            </Link>
            <Link to="/exams/generate" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[12px] border border-accent bg-accent px-4 py-2 text-sm font-semibold text-navy transition hover:brightness-105 hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              Generate another set
            </Link>
          </>
        }
      />
      <div className="mb-[18px] grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Forms" size="lg" value={forms} sub={totals.hasStatus ? `${totals.active} active \u00b7 ${totals.drafts.length} ${totals.drafts.length === 1 ? 'draft' : 'drafts'}` : undefined} />
        {totals.hasPerForm && (
          <KpiCard
            label="Students took"
            size="lg"
            value={totals.took}
            unit={<span className="text-muted"> / {totals.assigned}</span>}
            track={<Track value={tookPct} label="Students who took the set" />}
            sub={unpublished.length > 0 ? `${unpublished.length === 1 ? 'Form' : 'Forms'} ${unpublished.join(', ')} not yet published` : undefined}
          />
        )}
        {totals.mean != null && <KpiCard label="Average, all forms" size="lg" value={totals.mean.toFixed(1)} unit="%" />}
        {totals.spread != null && (
          <KpiCard
            label="Spread between forms"
            size="lg"
            value={totals.spread.toFixed(1)}
            unit={<span> pts</span>}
            sub={totals.spread < 5 ? 'Forms are evenly matched (under 5 pts)' : 'Forms differ by 5 pts or more'}
          />
        )}
      </div>
      <div className={`grid items-start gap-[18px] ${hasBlueprint ? 'xl:grid-cols-[2fr_1fr]' : ''}`}>
        <PanelCard
          aria-label="Forms in this set"
          title="Forms in this set"
          subtitle="Same chapters and difficulty mix, different questions"
          actions={forms > 1 && totals.hasPerForm ? (
            <Segmented label="View" value={view} onChange={setView} options={[{ key: 'forms', label: 'Forms' }, { key: 'compare', label: 'Compare' }]} />
          ) : undefined}
        >
          {forms === 0 ? (
            <EmptyState icon={<FaFileAlt />} title="No forms in this set" body="Generate an exam to add forms to a set." />
          ) : view === 'compare' ? (
            <CompareView forms={data.memberships} />
          ) : (
            <ul>
              {pg.pageItems.map(m => {
                const hasData = m.took != null && m.assigned != null;
                const meta = [
                  m.section_names && m.section_names.length > 0 ? `${m.section_names.length === 1 ? 'Section' : 'Sections'} ${m.section_names.join(', ')}` : null,
                  m.question_count != null ? `${m.question_count} questions` : null,
                  m.time_given != null ? `${m.time_given} min` : null,
                  m.status === 'Draft' ? 'not published' : (m.due_at ? `due ${dmy(m.due_at)}` : null),
                ].filter(Boolean).join(' \u00b7 ');
                return (
                  <li key={m.exam_id} className="flex flex-wrap items-center gap-3 border-b border-hairline py-3.5 last:border-b-0">
                    <span aria-hidden className="form-l flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-navy font-display text-lg font-bold text-white">{m.form_label}</span>
                    <span className="min-w-0 flex-[1_1_220px]">
                      <Link to={`/exams/exam-detail/${m.exam_id}`} className="block break-words text-sm font-semibold text-ink no-underline hover:underline">{m.exam_name || `Form ${m.form_label}`}</Link>
                      {(m.exam_name || meta) && <span className="block text-xs text-muted">{meta || `Form ${m.form_label}`}</span>}
                    </span>
                    {hasData && (
                      <span className="min-w-[120px] flex-[0_1_170px]">
                        <span className="mb-1.5 flex justify-between text-xs"><span className="text-muted">Took</span><span className="font-mono font-medium">{m.took} / {m.assigned}</span></span>
                        <Track value={(m.assigned as number) > 0 ? ((m.took as number) / (m.assigned as number)) * 100 : 0} label={`Form ${m.form_label} took`} />
                      </span>
                    )}
                    {hasData && (
                      <span className="w-[70px] text-right">
                        <span className="block font-mono font-semibold">{m.avg_score != null ? `${m.avg_score.toFixed(1)}%` : '\u2014'}</span>
                        <span className="text-[11px] text-muted">average</span>
                      </span>
                    )}
                    {m.status && <Badge tone={m.status === 'Active' ? 'success' : 'draft'} className="w-16 justify-center">{m.status}</Badge>}
                    <span className="flex gap-0.5">
                      <Link to={`/exams/exam-detail/${m.exam_id}`} aria-label="Open" title="Open exam" className={RBTN}>
                        <ArrowRight size={18} strokeWidth={1.8} aria-hidden="true" />
                      </Link>
                      {hasData && (
                        <Link to={`/exams/report/${m.exam_id}`} aria-label={`Report for Form ${m.form_label}`} title="Open report" className={RBTN}>
                          <BarChart3 size={18} strokeWidth={1.8} aria-hidden="true" />
                        </Link>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {forms > 0 && view === 'forms' && <PagedFooter className="mt-3.5" label="Forms pagination" {...pg} onPage={pg.setPage} />}
        </PanelCard>
        {hasBlueprint && (
          <PanelCard
            aria-label="Set blueprint"
            title="Blueprint"
            subtitle="What this set was generated from."
            actions={data.generation_job_id != null ? <TextLink to={`/exams/generate/job/${data.generation_job_id}`}>Run details {'\u2192'}</TextLink> : undefined}
          >
            {facts.length > 0 && (
              <dl className="grid grid-cols-2 gap-2.5">
                {facts.map(f => (
                  <div key={f.label} className={TILE}>
                    <dt className={SL}>{f.label}</dt>
                    <dd className="mt-1.5 break-words font-display text-[17px] font-bold capitalize text-ink">{f.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {mix && (
              <div className={facts.length > 0 ? 'mt-5' : ''}>
                <p className={`mb-2 ${SL}`}>Difficulty mix</p>
                <div className="flex gap-1" role="img" aria-label={`Easy ${mix.easy}%, medium ${mix.medium}%, hard ${mix.hard}%`}>
                  <span className="h-2.5 rounded-[5px] bg-mist-fixed" style={{ flex: mix.easy }} />
                  <span className="h-2.5 rounded-[5px] bg-slate" style={{ flex: mix.medium }} />
                  <span className="h-2.5 rounded-[5px] bg-navy dark:bg-mist-fixed" style={{ flex: mix.hard }} />
                </div>
                <div className="mt-1.5 flex justify-between text-xs text-muted">
                  <span>Easy {mix.easy}%</span><span>Medium {mix.medium}%</span><span>Hard {mix.hard}%</span>
                </div>
              </div>
            )}
            {chapterRows.length > 0 && (
              <div className={facts.length > 0 || mix ? 'mt-5' : ''}>
                <p className={`mb-1 ${SL}`}>Chapters</p>
                <ul>
                  {chapterRows.map((c) => (
                    <li key={c.id} className="flex items-center gap-3 border-b border-hairline py-2 last:border-b-0">
                      <span className="min-w-0 flex-1 text-[13px]">{c.name ?? `Chapter ${c.id}`}</span>
                      <span className="font-mono text-xs text-muted">{c.count} q</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.creator_name && (
              <p className="mt-3.5 text-xs text-muted">Generated by {data.creator_name}{created ? ` on ${created}` : ''}</p>
            )}
          </PanelCard>
        )}
      </div>
    </>
  );
}
